import type { CurrentUser } from '@gamble-chess/contracts';
import type { JSX, ReactNode } from 'react';

import { NavLink, useRoute, type Route } from '../router.tsx';
import { focusRingClass } from '../ui/styles.ts';
import { AccountMenu } from './AccountMenu.tsx';
import { HealthFooter } from './HealthFooter.tsx';

/** The four destinations, in the order they are used. */
const destinations: readonly { readonly to: Route; readonly label: string }[] = [
  { to: '/', label: 'Owed' },
  { to: '/wagers', label: 'Wagers' },
  { to: '/history', label: 'History' },
  { to: '/setup', label: 'Setup' },
];

const pillClass = `inline-flex min-h-11 items-center rounded-full px-4 text-sm font-medium whitespace-nowrap ${focusRingClass}`;

function MainNav(): JSX.Element {
  const route = useRoute();

  return (
    <nav aria-label="Main" className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1">
      {destinations.map(({ to, label }) => (
        <NavLink
          key={to}
          to={to}
          className={
            route === to
              ? `${pillClass} bg-accent text-accent-ink`
              : `${pillClass} text-ink-muted hover:bg-surface-sunken`
          }
        >
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export interface AppShellProps {
  /** `null` while nobody is signed in: then there is nothing to navigate between. */
  readonly user: CurrentUser | null;
  readonly children: ReactNode;
}

/** The frame every screen sits in: who you are, where you can go, and how the system is. */
export function AppShell({ user, children }: AppShellProps): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-10 border-b border-border bg-canvas/95 backdrop-blur">
        <div className="mx-auto flex max-w-4xl flex-col gap-2 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <h1 className="text-xl font-semibold tracking-tight">
              <NavLink
                to="/"
                className={`inline-flex items-center gap-2 rounded-xs ${focusRingClass}`}
              >
                {/* The name beside it already says what this is (ADR-0026). */}
                <img src="/favicon.svg" alt="" width={28} height={28} className="size-7" />
                Gamble Chess
              </NavLink>
            </h1>
            {user === null ? null : <AccountMenu user={user} />}
          </div>
          {user === null ? null : <MainNav />}
        </div>
      </header>
      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col gap-4 px-4 py-6">
        {children}
      </main>
      <footer className="mx-auto w-full max-w-4xl px-4 pb-6">
        <HealthFooter />
      </footer>
    </div>
  );
}
